import { useCallback, useEffect, useRef, useState } from 'react'

interface Position {
    x: number
    y: number
}

type WidgetCorner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'

interface UseDraggableOptions {
    /** Which corner the FAB starts in (default: 'bottom-right') */
    initialCorner?: WidgetCorner
    /** Pixels gap from viewport edge (default: 20) */
    edgeOffset?: number
    /** Pixels of movement threshold before treating as drag vs click */
    dragThreshold?: number
    /** Called once when drag motion is first detected (after threshold) */
    onDragStart?: () => void
}

interface UseDraggableResult {
    position: Position
    isDragging: boolean
    /** Attach to the draggable element's onMouseDown / onTouchStart */
    onMouseDown: (e: React.MouseEvent) => void
    onTouchStart: (e: React.TouchEvent) => void
    /** Whether the pointer interaction was a drag (use to suppress click) */
    wasDrag: boolean
}

/**
 * Enables free-float dragging for an element.
 *
 * Uses position relative to viewport bottom-right so the FAB stays near its
 * initial corner on window resize. `position` is in {x, y} where x/y are
 * offsets from the left/top of the viewport (i.e. CSS left/top values).
 *
 * Click vs drag: if the pointer moves more than `dragThreshold` pixels before
 * mouseup/touchend, it is treated as a drag and `wasDrag` is set to true for
 * one render cycle so the click handler can bail out.
 */
export function useDraggable({
    initialCorner = 'bottom-right',
    edgeOffset = 20,
    dragThreshold = 5,
    onDragStart,
}: UseDraggableOptions = {}): UseDraggableResult {
    const getInitialPosition = useCallback((): Position => {
        const isRight = initialCorner === 'bottom-right' || initialCorner === 'top-right'
        const isBottom = initialCorner === 'bottom-right' || initialCorner === 'bottom-left'
        return {
            x: isRight ? window.innerWidth - edgeOffset - 60 : edgeOffset,
            y: isBottom ? window.innerHeight - edgeOffset - 60 : edgeOffset,
        }
    }, [initialCorner, edgeOffset])

    const [position, setPosition] = useState<Position>(getInitialPosition)
    const [isDragging, setIsDragging] = useState(false)
    const [wasDrag, setWasDrag] = useState(false)

    const dragStart = useRef<{ clientX: number; clientY: number; originX: number; originY: number } | null>(null)
    const hasMoved = useRef(false)

    const startDrag = useCallback((clientX: number, clientY: number, currentPos: Position) => {
        hasMoved.current = false
        dragStart.current = { clientX, clientY, originX: currentPos.x, originY: currentPos.y }
    }, [])

    const onMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return
        e.preventDefault()
        setPosition(prev => {
            startDrag(e.clientX, e.clientY, prev)
            return prev
        })
        setWasDrag(false)
    }, [startDrag])

    const onTouchStart = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0]
        setPosition(prev => {
            startDrag(touch.clientX, touch.clientY, prev)
            return prev
        })
        setWasDrag(false)
    }, [startDrag])

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!dragStart.current) return
            const dx = e.clientX - dragStart.current.clientX
            const dy = e.clientY - dragStart.current.clientY

            if (!hasMoved.current && Math.hypot(dx, dy) > dragThreshold) {
                hasMoved.current = true
                setIsDragging(true)
                onDragStart?.()
            }

            if (hasMoved.current) {
                const newX = Math.max(0, Math.min(window.innerWidth - 60, dragStart.current.originX + dx))
                const newY = Math.max(0, Math.min(window.innerHeight - 60, dragStart.current.originY + dy))
                setPosition({ x: newX, y: newY })
            }
        }

        const handleMouseUp = () => {
            if (!dragStart.current) return
            if (hasMoved.current) {
                setWasDrag(true)
                // Reset wasDrag after one event loop tick so click handler can read it
                setTimeout(() => setWasDrag(false), 0)
            }
            dragStart.current = null
            hasMoved.current = false
            setIsDragging(false)
        }

        const handleTouchMove = (e: TouchEvent) => {
            if (!dragStart.current) return
            const touch = e.touches[0]
            const dx = touch.clientX - dragStart.current.clientX
            const dy = touch.clientY - dragStart.current.clientY

            if (!hasMoved.current && Math.hypot(dx, dy) > dragThreshold) {
                hasMoved.current = true
                setIsDragging(true)
                onDragStart?.()
            }

            if (hasMoved.current) {
                e.preventDefault()
                const newX = Math.max(0, Math.min(window.innerWidth - 60, dragStart.current.originX + dx))
                const newY = Math.max(0, Math.min(window.innerHeight - 60, dragStart.current.originY + dy))
                setPosition({ x: newX, y: newY })
            }
        }

        const handleTouchEnd = () => {
            if (!dragStart.current) return
            if (hasMoved.current) {
                setWasDrag(true)
                setTimeout(() => setWasDrag(false), 0)
            }
            dragStart.current = null
            hasMoved.current = false
            setIsDragging(false)
        }

        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)
        window.addEventListener('touchmove', handleTouchMove, { passive: false })
        window.addEventListener('touchend', handleTouchEnd)

        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
            window.removeEventListener('touchmove', handleTouchMove)
            window.removeEventListener('touchend', handleTouchEnd)
        }
    }, [dragThreshold])

    return { position, isDragging, onMouseDown, onTouchStart, wasDrag }
}
